import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { GetLocaleTextParams, GridOptions } from 'ag-grid-community';

import { translateAuditReportState } from '../../services/after-sales/after-sales-functions';
import { translateBookingState } from '../../services/booking/recourse-booking-functions';
import {
  getCampaignStateAndTranslateDescription,
  translateCampaignStateType,
} from '../../services/recourse-campaign/recourse-campaign-functions';
import { UserService } from '../../services/user-service/user.service';
import { BookingTypeEnum } from '../enums/booking-type-enum';
import { CreationTypeEnum } from '../enums/creation-type-enum';
import { RecourseTypeIdEnum } from '../enums/recourse-type-id-enum';

const getYesAndNoTranslation = (value: boolean, translateService: TranslateService): string =>
  translateService.instant(value ? 'YES' : 'NO');

@Injectable({
  providedIn: 'root',
})
export class AgGridService {
  public newDefaultGridOptions(): GridOptions<any> {
    const gridOptions: GridOptions = {
      rowHeight: 35,
      enableCellTextSelection: true,
      ensureDomOrder: true,
    };
    return gridOptions;
  }

  public translateBookingType(translateService: TranslateService) {
    return params => {
      if (params.data.bookingType === BookingTypeEnum.CREDIT) {
        return translateService.instant(BookingTypeEnum.CREDIT);
      } else if (params.data.bookingType === BookingTypeEnum.DEBIT) {
        return translateService.instant(BookingTypeEnum.DEBIT);
      }
    };
  }

  public translateCampaignStateTypeCell(translateService: TranslateService) {
    return params => translateCampaignStateType(params.data.campaignStateType, translateService);
  }

  public getCampaignStateAndTranslateDescription(translateService: TranslateService) {
    return params => getCampaignStateAndTranslateDescription(params.data.fkCampaignStateId, translateService);
  }

  public getRecourseType(translateService: TranslateService) {
    return params => {
      switch (params.data.recourseTypeId) {
        case RecourseTypeIdEnum.RECOURSE: {
          return translateService.instant('RECOURSE');
        }
        case RecourseTypeIdEnum.CAMPAIGN: {
          return translateService.instant('CAMPAIGN');
        }
        case RecourseTypeIdEnum.RAD: {
          return translateService.instant('RAD');
        }
        case RecourseTypeIdEnum.AFTERSALES: {
          return translateService.instant('AFTERSALES');
        }
        default: {
          return '';
        }
      }
    };
  }

  public getCampaignStateGroupname(translateService: TranslateService) {
    return (param: any) => this.translateCampaignStateGroupname(param.data.campaignState, translateService);
  }

  public translateCampaignStateGroupname(campaignState: string, translateService: TranslateService): string {
    return campaignState ? translateService.instant(campaignState) : '';
  }

  public translate(userService: UserService, translateService: TranslateService) {
    return (localeTextParam: GetLocaleTextParams) => {
      if (userService.userLanguage === 'en') {
        return localeTextParam.defaultValue;
      }
      const propertyKey = `ag-grid.${localeTextParam.key}`;
      const localeValue = translateService.instant(propertyKey);
      return localeValue === propertyKey ? localeTextParam.defaultValue : localeValue;
    };
  }

  public convertCreationType() {
    return params => {
      if (params.data.manualBooking === '1') {
        return CreationTypeEnum.MANUAL;
      } else if (params.data.manualBooking === '0') {
        return CreationTypeEnum.AUTOMATIC;
      } else {
        return '';
      }
    };
  }

  public getBookingStateName(translateService: TranslateService) {
    return (params: any) => translateBookingState(params.data.bookingStateId, translateService);
  }

  public translateApprovalsHistoryAction(translateService: TranslateService) {
    return (params: any) => translateService.instant(params.data.action);
  }

  public getEurAmountWithBookingTypeCheck() {
    return (params: any) => {
      let eurAmount = params.data.partialPaymentItems[0].eurAmount;
      if (params.data.bookingType === BookingTypeEnum.CREDIT && eurAmount > 0) {
        eurAmount = -eurAmount; // display as negative value
      }
      return eurAmount;
    };
  }

  public getDueDate() {
    return (params: any) => params.data.partialPaymentItems[0].dueDate;
  }

  public translateAuditReportState(translateService: TranslateService) {
    return (params: any) => translateAuditReportState(params.data.reportState, translateService);
  }

  public getAuditReportYesAndNoTranslation(translateService: TranslateService) {
    return (params: any) => getYesAndNoTranslation(params.data.recourseRelevant, translateService);
  }

  public getCampaignOriginalYesAndNoTranslation(translateService: TranslateService) {
    return (params: any) => getYesAndNoTranslation(params.data.campaignOrigin, translateService);
  }

  /**
   * For translating some items from the result list which get shown in the table
   * @param translateService
   * @param currentField
   */
  public getTranslationForReportingCampaign(translateService: TranslateService, currentField: string) {
    const FIELD_CAMPAIGNSTATETYPE = 'campaignStateType';
    const FIELD_REPORTINGVAQ = 'reportingVaq';
    const FIELD_CAMPAIGNSTATE = 'campaignState';

    /* eslint-disable complexity */
    return (params: any) => {
      switch (currentField) {
        case FIELD_CAMPAIGNSTATETYPE: {
          switch (params.data.campaignStateType) {
            case 'INTERNAL': {
              return translateService.instant('INTERNAL');
            }
            case 'EXTERNAL': {
              return translateService.instant('EXTERNAL');
            }
            default: {
              return '';
            }
          }
        }
        case FIELD_REPORTINGVAQ: {
          switch (params.data.reportingVaq) {
            case '0': {
              return translateService.instant('NO');
            }
            case '1': {
              return translateService.instant('YES');
            }
            default: {
              return '';
            }
          }
        }
        case FIELD_CAMPAIGNSTATE: {
          switch (params.data.campaignState) {
            case 'IN_NEGOTIATION': {
              return translateService.instant('IN_NEGOTIATION');
            }
            case 'INTERNAL_FAULT': {
              return translateService.instant('INTERNAL_FAULT');
            }
            case 'OPEN': {
              return translateService.instant('OPEN');
            }
            case 'COMPLETED': {
              return translateService.instant('COMPLETED');
            }
            case 'PREPARE_DATA': {
              return translateService.instant('PREPARE_DATA');
            }
            case 'CANCELLED': {
              return translateService.instant('CANCELLED');
            }
            default: {
              return '';
            }
          }
        }
        // No default
      }
    };
    /* eslint-enable */
  }
}
